import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ApiError, describeError } from "../../api/client";
import type { AdminContactDetail } from "../../api/types";
import { RequireRole } from "../../auth/RequireRole";
import { Combobox } from "../../components/Combobox";
import { DetailGroup } from "../../components/DetailGroup";
import { Field } from "../../components/Field";
import { FeedbackPanel } from "../../components/FeedbackPanel";
import { Modal } from "../../components/Modal";
import { ModalCloseButton } from "../../components/ModalCloseButton";
import { NotFoundCard } from "../../components/NotFoundCard";
import { StatusPill } from "../../components/StatusPill";
import { useToast } from "../../components/ToastProvider";
import { formatDateTime } from "../../formatting/date";
import { invalidateExact } from "../../state/queryClient";
import { useMutation } from "../../state/useMutation";
import { buildAllowedSearchParams } from "../../state/routeState";
import { useQuery } from "../../state/useQuery";
import { getContact, suppressContact, unsuppressContact } from "./api";
import {
  avatarColorsFor,
  formatAuditEntry,
  formatCargoEmpresa,
  formatNivelLabel,
  initialsFor,
  resolveContactStatus,
  suppressionMotivoLabel,
  SUPPRESSION_MOTIVOS,
} from "./derived";
import { formatActor } from "../../formatting/actor";
import { CONTACT_LIST_ALLOWED_KEYS } from "./filters";

type ActiveModal = "suppress" | "unsuppress" | null;

/**
 * `/contactos/:contactId` — REDISENO-PANEL.md §7.2. "‹ Contactos" back link
 * (preserving the list's allow-listed filter params), a header card (64px
 * avatar, name + `StatusPill`, "cargo · empresa", and the single consequential
 * action for this contact's current state), an amber suppressed notice, a
 * "Datos" card, and a "Historial de supresiones" timeline. Suppress/Reactivate
 * are each a single `Modal` step (intro + the real action), same "the modal
 * itself is the confirmation" convention `CampaignDetailScreen`'s own
 * `CancelModal`/`DispatchModal` already established — not a second nested
 * `ConfirmDialog` after an inline form.
 *
 * Nonexistent and foreign-tenant IDs render the IDENTICAL local not-found
 * state (the backend scopes `GetDetailAsync` by `client_id`, both cases
 * produce the same `404`).
 */
export function ContactDetailScreen() {
  const { contactId } = useParams<{ contactId: string }>();
  const [searchParams] = useSearchParams();
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const [motivo, setMotivo] = useState<string>(SUPPRESSION_MOTIVOS[0].value);
  const { showToast } = useToast();

  const contextSuffix = useMemo(() => {
    const allowed = buildAllowedSearchParams(searchParams.toString(), CONTACT_LIST_ALLOWED_KEYS);
    const asString = allowed.toString();
    return asString ? `?${asString}` : "";
  }, [searchParams]);

  const { data, error, loading, refetch } = useQuery<AdminContactDetail>({
    key: ["contact", contactId],
    queryFn: () => getContact(contactId as string),
  });

  const suppressMutation = useMutation({
    mutationFn: (args: { motivo: string }) => suppressContact(contactId as string, args),
    invalidateKeys: [["contact", contactId]],
  });

  const unsuppressMutation = useMutation({
    mutationFn: () => unsuppressContact(contactId as string),
    invalidateKeys: [["contact", contactId]],
  });

  // `Modal` binds its Escape/Tab handlers ONCE at mount (same documented
  // convention `ConfirmDialog` already uses) -- the `onClose` closure it
  // captures is whichever one was passed on that first render, so it must
  // read "is a submit in flight" from a REF, never from `mutation.status`
  // directly, or a later Escape press would act on the STALE idle value
  // captured when the modal first opened (MUST-apply lesson: Escape must
  // never close a modal while it is submitting).
  const suppressPendingRef = useRef(false);
  suppressPendingRef.current = suppressMutation.status === "pending";
  const unsuppressPendingRef = useRef(false);
  unsuppressPendingRef.current = unsuppressMutation.status === "pending";

  const closeSuppressModal = () => {
    if (!suppressPendingRef.current) {
      setActiveModal(null);
      // RDD (review-reliability, WARNING, R3-003 / R3-stale-error-on-reopen):
      // the success path (onSubmit below) already resets `motivo` and clears
      // the mutation's error, but Cancelar -- and a failed attempt the
      // operator then cancels out of -- left both the stale motivo selection
      // AND the previous attempt's error in state, both of which would
      // silently reappear the next time this modal opens.
      setMotivo(SUPPRESSION_MOTIVOS[0].value);
      suppressMutation.reset();
    }
  };

  const closeUnsuppressModal = () => {
    if (!unsuppressPendingRef.current) {
      setActiveModal(null);
      unsuppressMutation.reset();
    }
  };

  const isNotFound = error instanceof ApiError && error.kind === "notFound";

  // Same eviction primitive `SegmentDetailScreen.tsx` uses: a not-found
  // branch alone only protects the CURRENT render; without evicting the
  // shared cache entry, navigating away and back to the same URL would
  // rehydrate the deleted contact from the still-cached, now-stale entry.
  const evictedForContactIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (isNotFound && contactId && evictedForContactIdRef.current !== contactId) {
      evictedForContactIdRef.current = contactId;
      invalidateExact([["contact", contactId]]);
    }
  }, [isNotFound, contactId]);

  // An UNCACHED first visit's real initial phase is `idle` (no data, no
  // error, `loading` still `false`) — a `loading`-only check misses the
  // render `RouteFocus.tsx`'s one-shot `querySelector('h1')` inspects.
  const isPending = loading || (!data && error === undefined);

  if (isPending) {
    return (
      <>
        <h1>Contacto</h1>
        <p role="status">Cargando contacto…</p>
      </>
    );
  }

  // A FRESH not-found response must always win over any stale cached `data`
  // from an earlier successful fetch — checked BEFORE the `!data` branch.
  if (isNotFound) {
    return (
      <NotFoundCard
        ariaLabel="Contacto no encontrado"
        title="Contacto no encontrado"
        description="El contacto solicitado no existe o no está disponible para esta cuenta."
        secondaryAction={{ label: "Volver a Contactos", to: `/contactos${contextSuffix}` }}
        primaryAction={{ label: "Ir al inicio", to: "/" }}
      />
    );
  }

  if (!data) {
    return (
      <FeedbackPanel
        variant="error"
        message={describeError(error, "No se pudo cargar el contacto.")}
        onRetry={refetch}
      />
    );
  }

  const { contact, activeSuppression, auditHistory } = data;
  const { label, tone } = resolveContactStatus(contact.estado);
  const colors = avatarColorsFor(contact.contactId);

  return (
    <section aria-label="Detalle de contacto" className="v2-contact-detail">
      <p>
        <Link to={`/contactos${contextSuffix}`} className="v2-contact-detail__back">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M15 6l-6 6 6 6" />
          </svg>
          Contactos
        </Link>
      </p>

      <header className="v2-contact-detail__header">
        <span
          className="v2-contact-detail__avatar"
          style={{ background: colors.bg, color: colors.fg }}
          aria-hidden="true"
        >
          {initialsFor(contact.nombre)}
        </span>
        <div className="v2-contact-detail__header-identity">
          <div className="v2-contact-detail__header-title">
            <h1>{contact.nombre}</h1>
            <StatusPill label={label} tone={tone} />
          </div>
          <span className="v2-contact-detail__header-subtitle">{formatCargoEmpresa(contact.cargo, contact.empresa)}</span>
        </div>
        <RequireRole role="Operador">
          {activeSuppression ? (
            <button type="button" className="v2-u-target-min" onClick={() => setActiveModal("unsuppress")}>
              Reactivar contacto
            </button>
          ) : (
            <button
              type="button"
              className="v2-u-target-min v2-u-danger"
              onClick={() => setActiveModal("suppress")}
            >
              Suprimir contacto
            </button>
          )}
        </RequireRole>
      </header>

      {activeSuppression && (
        <p className="v2-contact-detail__suppressed-notice" role="status">
          Este contacto no recibe campañas. Motivo: {suppressionMotivoLabel(activeSuppression.motivo)}.
        </p>
      )}

      <div className="v2-contact-detail__cards">
      <DetailGroup
        title="Datos"
        items={[
          { label: "Email", value: contact.email, mono: true },
          { label: "Empresa", value: contact.empresa },
          { label: "Cargo", value: contact.cargo },
          { label: "Nivel", value: formatNivelLabel(contact.nivelJerarquico) },
          ...(contact.industria ? [{ label: "Industria", value: contact.industria }] : []),
        ]}
      />

      <section aria-label="Historial de supresiones" className="v2-contact-detail__audit-card">
        <div className="v2-contact-detail__card-header">
          <h2>Historial de supresiones</h2>
        </div>
        {auditHistory.length === 0 ? (
          <p className="v2-contact-detail__audit-empty">Sin movimientos registrados.</p>
        ) : (
          <ol className="v2-contact-detail__audit">
            {auditHistory.map((entry) => {
              const presentation = formatAuditEntry(entry);
              return (
                <li key={entry.auditId}>
                  <span
                    className="v2-contact-detail__audit-dot"
                    style={{ borderColor: presentation.dotColor }}
                    aria-hidden="true"
                  />
                  <span className="v2-contact-detail__audit-body">
                    <span>{presentation.text}</span>
                    <span>
                      {formatDateTime(entry.timestamp)} · {formatActor(entry.actorUpn)}
                    </span>
                  </span>
                </li>
              );
            })}
          </ol>
        )}
      </section>
      </div>

      {activeModal === "suppress" && (
        <SuppressModal
          contactName={contact.nombre}
          motivo={motivo}
          onMotivoChange={setMotivo}
          submitting={suppressMutation.status === "pending"}
          error={suppressMutation.error}
          onClose={closeSuppressModal}
          onSubmit={() =>
            suppressMutation.mutate({ motivo }).then(
              () => {
                setActiveModal(null);
                // Codex PR #386 review: `motivo` otherwise keeps the last
                // chosen value in component state forever, so the NEXT time
                // this contact (or another one, same mounted screen) opens
                // the Suprimir modal it would silently preselect whatever
                // was picked last time instead of the real default.
                setMotivo(SUPPRESSION_MOTIVOS[0].value);
                showToast("Contacto suprimido");
              },
              () => {
                // suppressMutation.error (rendered inside the modal) is the
                // single source of truth for the failure UI.
              },
            )
          }
        />
      )}

      {activeModal === "unsuppress" && (
        <ReactivateModal
          contactName={contact.nombre}
          submitting={unsuppressMutation.status === "pending"}
          error={unsuppressMutation.error}
          onClose={closeUnsuppressModal}
          onSubmit={() =>
            unsuppressMutation.mutate(undefined as never).then(
              () => {
                setActiveModal(null);
                showToast("Contacto reactivado");
              },
              () => {},
            )
          }
        />
      )}
    </section>
  );
}

/** §7.2 "Modal Suprimir": intro naming the contact + consequence, the 4 real motivos, red "Suprimir" CTA. */
function SuppressModal({
  contactName,
  motivo,
  onMotivoChange,
  submitting,
  error,
  onClose,
  onSubmit,
}: {
  contactName: string;
  motivo: string;
  onMotivoChange: (motivo: string) => void;
  submitting: boolean;
  error: unknown;
  onClose: () => void;
  onSubmit: () => Promise<unknown>;
}) {
  return (
    <Modal ariaLabel="Suprimir contacto" onClose={onClose}>
      <div className="v2-modal-v2 v2-modal-v2--w500">
        <header className="v2-modal-v2__header v2-modal-v2__header--form">
          <h2>Suprimir contacto</h2>
          <ModalCloseButton onClose={onClose} disabled={submitting} />
        </header>
        <form
          className="v2-modal-v2__body"
          onSubmit={(event) => {
            event.preventDefault();
            void onSubmit();
          }}
        >
          <p className="v2-modal-v2__hint--lead">
            {contactName} dejará de recibir campañas mientras la supresión esté activa.
          </p>
          <Field label="Motivo" htmlFor="contact-suppress-motivo">
            <Combobox
              id="contact-suppress-motivo"
              searchable={false}
              value={motivo}
              onChange={onMotivoChange}
              disabled={submitting}
              options={SUPPRESSION_MOTIVOS.map((option) => ({ value: option.value, label: option.label }))}
            />
          </Field>
          {error !== undefined && <p role="alert">{describeError(error, "No se pudo suprimir el contacto.")}</p>}
          <footer className="v2-modal-v2__footer">
            <button type="button" className="v2-u-target-min v2-btn-secondary" onClick={onClose} disabled={submitting}>
              Cancelar
            </button>
            <button
              type="submit"
              className="v2-u-target-min v2-u-danger v2-contact-detail__suppress-confirm"
              disabled={submitting}
              aria-busy={submitting}
            >
              {submitting ? "Suprimiendo…" : "Suprimir"}
            </button>
          </footer>
          {submitting && <p role="status">Suprimiendo…</p>}
        </form>
      </div>
    </Modal>
  );
}

/** §7.2 "Modal Reactivar": intro naming the contact, "Reactivar" CTA. */
function ReactivateModal({
  contactName,
  submitting,
  error,
  onClose,
  onSubmit,
}: {
  contactName: string;
  submitting: boolean;
  error: unknown;
  onClose: () => void;
  onSubmit: () => Promise<unknown>;
}) {
  return (
    <Modal ariaLabel="Reactivar contacto" onClose={onClose}>
      <div className="v2-modal-v2 v2-modal-v2--w480">
        <header className="v2-modal-v2__header v2-modal-v2__header--form">
          <h2>Reactivar contacto</h2>
          <ModalCloseButton onClose={onClose} disabled={submitting} />
        </header>
        <div className="v2-modal-v2__body">
          <p className="v2-modal-v2__hint--lead">{contactName} volverá a recibir campañas.</p>
          {error !== undefined && <p role="alert">{describeError(error, "No se pudo reactivar el contacto.")}</p>}
        </div>
        <footer className="v2-modal-v2__footer">
          <button type="button" className="v2-u-target-min v2-btn-secondary" onClick={onClose} disabled={submitting}>
            Cancelar
          </button>
          <button
            type="button"
            className="v2-u-target-min v2-btn-primary"
            disabled={submitting}
            aria-busy={submitting}
            onClick={() => void onSubmit()}
          >
            {submitting ? "Reactivando…" : "Reactivar"}
          </button>
        </footer>
        {submitting && <p role="status">Reactivando…</p>}
      </div>
    </Modal>
  );
}
